import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { BarChart3, Loader2, TrendingUp, ArrowDownLeft, Percent, Building2, Download, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Brand palette
const COLOR_NAVY = '#1B3A4B';
const COLOR_GOLD = '#D4A745';
const COLOR_EMERALD = '#0F7B3F';
const COLOR_AMBER = '#C2410C';
const PIE_COLORS = [COLOR_NAVY, COLOR_GOLD, COLOR_EMERALD, COLOR_AMBER, '#6366F1', '#EC4899', '#10B981', '#F59E0B'];

const StatCard = ({ icon: Icon, label, value, color = 'text-[#1B3A4B]', bg = 'bg-[#1B3A4B]/8' }) => (
  <Card className="hover:shadow-md transition-shadow">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
          <p className={`text-lg font-bold ${color} truncate tabular-nums`}>{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const CurrencyTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-gray-600">{p.name}:</span>
          <span className="font-semibold tabular-nums">{formatCurrency(p.value, 'XOF')}</span>
        </p>
      ))}
    </div>
  );
};

const CountTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label || payload[0]?.name}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs">
          <span className="text-gray-600">{p.name}:</span>{' '}
          <span className="font-semibold tabular-nums">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const AgencyStats = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get('/dashboard/agency-stats');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (!isAdmin) {
    return (
      <div className="text-center py-20" data-testid="agency-stats-forbidden">
        <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Reserve aux administrateurs</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" /></div>;
  }

  if (!data) return null;

  const handleDownloadCSV = () => {
    const rows = [
      ['Mois', 'Encaissement N (XOF)', 'Encaissement N-1 (XOF)', 'Commission (XOF)', 'Retraits (XOF)'],
      ...data.monthly_collection.map((m, i) => [
        m.month,
        m.encaissement,
        m.encaissement_n1 || 0,
        m.commission,
        data.monthly_withdrawals[i]?.retraits || 0,
      ]),
    ];
    const csv = rows.map(r => r.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statistiques_agence_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('CSV telecharge');
  };

  const formatYAxisXof = (v) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
    return v;
  };

  return (
    <div className="space-y-6" data-testid="agency-stats-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-[#1B3A4B]" />
            Statistiques de l&apos;agence
          </h1>
          <p className="text-gray-500 mt-1">
            Vue analytique sur 12 mois - {data.period_label}
          </p>
        </div>
        <Button onClick={handleDownloadCSV} variant="outline" className="border-[#D4A745] text-[#1B3A4B] hover:bg-[#D4A745]/10" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-2" />
          Exporter CSV
        </Button>
      </div>

      {/* Headline KPIs (12 months totals) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={ArrowDownLeft}
          label="Encaisse (12 mois)"
          value={formatCurrency(data.totals_12m.collected, 'XOF')}
          color="text-[#0F7B3F]" bg="bg-emerald-50"
        />
        <StatCard
          icon={data.totals_12m.yoy_growth_percentage >= 0 ? TrendingUp : TrendingDown}
          label="Croissance N vs N-1"
          value={`${data.totals_12m.yoy_growth_percentage > 0 ? '+' : ''}${data.totals_12m.yoy_growth_percentage}%`}
          color={data.totals_12m.yoy_growth_percentage >= 0 ? 'text-[#0F7B3F]' : 'text-red-700'}
          bg={data.totals_12m.yoy_growth_percentage >= 0 ? 'bg-emerald-50' : 'bg-red-50'}
        />
        <StatCard
          icon={Percent}
          label="Commissions (12 mois)"
          value={formatCurrency(data.totals_12m.commission, 'XOF')}
          color="text-[#9C7710]" bg="bg-[#D4A745]/15"
        />
        <StatCard
          icon={TrendingUp}
          label="Retraits (12 mois)"
          value={formatCurrency(data.totals_12m.withdrawn, 'XOF')}
          color="text-[#C2410C]" bg="bg-amber-50"
        />
      </div>

      {/* Monthly collection vs commission (grouped bars / tube chart) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-[#1B3A4B] flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#D4A745]" />
            Encaissements N vs N-1 vs Commissions (12 mois)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full h-80" data-testid="chart-monthly-collection">
            <ResponsiveContainer>
              <BarChart data={data.monthly_collection} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradCollect" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLOR_NAVY} stopOpacity={1} />
                    <stop offset="100%" stopColor={COLOR_NAVY} stopOpacity={0.65} />
                  </linearGradient>
                  <linearGradient id="gradCollectN1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#94A3B8" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="#CBD5E1" stopOpacity={0.7} />
                  </linearGradient>
                  <linearGradient id="gradComm" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLOR_GOLD} stopOpacity={1} />
                    <stop offset="100%" stopColor={COLOR_GOLD} stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={formatYAxisXof} />
                <Tooltip content={<CurrencyTooltip />} cursor={{ fill: 'rgba(212,167,69,0.06)' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                <Bar dataKey="encaissement_n1" name="Encaissements N-1" fill="url(#gradCollectN1)" radius={[6, 6, 0, 0]} />
                <Bar dataKey="encaissement" name="Encaissements N" fill="url(#gradCollect)" radius={[6, 6, 0, 0]} />
                <Bar dataKey="commission" name="Commissions" fill="url(#gradComm)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Withdrawals by month */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1B3A4B]">Retraits proprietaires (12 mois)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-64" data-testid="chart-withdrawals">
              <ResponsiveContainer>
                <BarChart data={data.monthly_withdrawals} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradWd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLOR_AMBER} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLOR_AMBER} stopOpacity={0.65} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickFormatter={formatYAxisXof} />
                  <Tooltip content={<CurrencyTooltip />} cursor={{ fill: 'rgba(194,65,12,0.06)' }} />
                  <Bar dataKey="retraits" name="Retraits" fill="url(#gradWd)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Properties by type (horizontal bars) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1B3A4B]">Repartition des biens par type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-64" data-testid="chart-properties-type">
              <ResponsiveContainer>
                <BarChart data={data.properties_by_type} layout="vertical" margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <YAxis type="category" dataKey="type" tick={{ fontSize: 11, fill: '#374151' }} width={110} />
                  <Tooltip content={<CountTooltip />} cursor={{ fill: 'rgba(27,58,75,0.06)' }} />
                  <Bar dataKey="count" name="Nombre" fill={COLOR_NAVY} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top 5 properties */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1B3A4B]">Top 5 biens les plus rentables</CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_collected_properties.length === 0 ? (
              <p className="text-center text-gray-400 py-8 text-sm">Aucune donnee</p>
            ) : (
              <div className="space-y-3" data-testid="top-properties">
                {data.top_collected_properties.map((p, i) => {
                  const max = data.top_collected_properties[0].total || 1;
                  const widthPct = Math.max(8, (p.total / max) * 100);
                  return (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-6 h-6 rounded-md bg-[#D4A745]/15 text-[#9C7710] flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="font-medium text-gray-900 truncate">{p.property}</span>
                          {p.code && <span className="text-[10px] font-mono text-gray-400">{p.code}</span>}
                        </div>
                        <span className="font-bold text-[#0F7B3F] tabular-nums text-xs flex-shrink-0">
                          {formatCurrency(p.total, 'XOF')}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${widthPct}%`,
                            background: `linear-gradient(90deg, ${COLOR_NAVY}, ${COLOR_GOLD})`,
                          }}
                        />
                      </div>
                      <p className="text-[10px] text-gray-400">{p.payments} paiement(s)</p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Property status pie */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1B3A4B] flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#D4A745]" />
              Statut des biens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-64" data-testid="chart-status">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.properties_by_status}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {data.properties_by_status.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CountTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgencyStats;
